import React, { useState, useEffect } from 'react';
import { exportJSON, uid } from '../db';
import {
  toast, Badge, Btn, Card, CardHead, Modal, ModalFooter,
  Field, Input, Select, Tabs, NoData,
} from '../ui.jsx';
import {
  adminUploadStudentDocument, reviewStudentDocument,
  getStudentDocument, getStudentDocuments, markIdCardGenerated,
} from '../storage';
import {
  buildCard, buildCardBack, buildTC, buildCC, buildBonafide,
  buildTeacherCard, buildTeacherCardBack,
  printCard, printClassCards, printTC, printCC, printBonafide,
  printTeacherCard, CARD_DIMS,
} from '../print.js';
import { fmtDob } from '../utils/fmtDob';
import DocPreview from '../components/DocPreview';
import CertRegistry from './CertRegistry';

const LOGO_SRC = process.env.PUBLIC_URL + '/logo.png';



function Documents({ db, save, activeSessionId }) {
  // Shared
  const [docTab, setDocTab] = useState('id'); // 'id' | 'tc' | 'cc'
  const [logoErr, setLogoErr] = useState(false);
  const logo = logoErr ? null : LOGO_SRC;
  const classes = db.classes.map(c => c.name);
  const getAtt = (sid) => { let p = 0, t = 0; Object.values(db.att).forEach(d => { if (d[sid] !== undefined) { t++; if (d[sid] === 'P' || d[sid] === 'L') p++; } }); return { p, t }; };

  // ID Card state
  const [selCls, setSelCls] = useState('');
  const [selStu, setSelStu] = useState('');
  const [selAll, setSelAll] = useState(false);
  const [phone, setPhone] = useState(db.settings.phone || '9997360040, 8650616990');
  const [year, setYear] = useState(db.settings.year || '2025-2026');
  const [prin, setPrin] = useState(db.settings.prin || '');
  const [theme, setTheme] = useState('blue');

  // Teacher ID Card state
  const [tidStu, setTidStu] = useState('');
  const [tidTheme, setTidTheme] = useState('blue');

  const clsStudents = selCls ? db.students.filter(s => s.cls === selCls) : db.students;
  const s = db.students.find(x => x.id === selStu);
  const photo = s ? (db.photos[s.id] || null) : null;
  const demoStu = { fn: 'Student', ln: 'Name', cls: '—', father: '—', mother: '—', city: '', addr: '', fphone: '', ph: '', dob: '', roll: '', admno: '', blood: '' };
  const previewStu = s || demoStu;
  const previewHTML = buildCard(previewStu, s ? photo : null, logo, phone, year, prin, theme, true);
  const previewBackHTML = buildCardBack(previewStu, logo, phone);

  // TC state — only fields NOT in student directory
  const [tcStu, setTcStu] = useState('');
  const [tcNo, setTcNo] = useState('');
  const [tcCls, setTcCls] = useState('');
  const [tcDt, setTcDt] = useState(new Date().toISOString().split('T')[0]);
  const [tcAdmDt, setTcAdmDt] = useState(''); // admission date — not stored on student
  const [tcLd, setTcLd] = useState('');        // last date of attendance
  const [tcRs, setTcRs] = useState("Parents' Transfer");
  const [tcCo, setTcCo] = useState('Good');
  const [tcFe, setTcFe] = useState('All Dues Cleared');
  // CC state
  const [ccStu, setCcStu] = useState(''); const [ccNo, setCcNo] = useState(''); const [ccDt, setCcDt] = useState(new Date().toISOString().split('T')[0]); const [ccCo, setCcCo] = useState('Good'); const [ccPu, setCcPu] = useState('General Purpose'); const [ccRm, setCcRm] = useState('');
  const [ccCls, setCcCls] = useState('');
  const [ccAttP, setCcAttP] = useState(''); const [ccAttT, setCcAttT] = useState('');
  const ccAttPct = ccAttT > 0 ? Math.round((parseInt(ccAttP) || 0) / parseInt(ccAttT) * 100) : 0;
  // Bonafide state
  const [bcStu, setBcStu] = useState(''); const [bcCls, setBcCls] = useState('');
  const [bcNo, setBcNo] = useState(''); const [bcDt, setBcDt] = useState(new Date().toISOString().split('T')[0]);
  const [bcPurpose, setBcPurpose] = useState('Aadhaar Card Enrollment');
  const [bcClsOverride, setBcClsOverride] = useState('');
  const [bcDobOverride, setBcDobOverride] = useState('');
  const [bcAdmnoOverride, setBcAdmnoOverride] = useState('');

  const genNextNo = (type) => {
    const year = new Date().getFullYear();
    const prefix = type === 'tc' ? 'TC' : type === 'bc' ? 'BC' : 'CC';
    const existing = (db.certRegistry || []).filter(r => r.type === type && r.no?.startsWith(`${prefix}-${year}`));
    const nums = existing.map(r => parseInt(r.no?.split('-').pop()) || 0);
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    return `${prefix}-${year}-${String(next).padStart(3, '0')}`;
  };

  const doTC = () => {
    if (!tcStu) { toast('Select student', 'err'); return; }
    const st = db.students.find(x => x.id === tcStu); if (!st) return;
    const { p, t } = getAtt(tcStu);
    const no = tcNo || genNextNo('tc');
    const record = {
      id: 'TC' + uid(), type: 'tc', no, dt: new Date(tcDt).toLocaleDateString('en-IN'),
      stuId: st.id, name: st.fn + (st.ln ? ' ' + st.ln : ''), cls: st.cls, roll: st.roll || '',
      admno: st.admno || '', reason: tcRs, conduct: tcCo, generatedAt: new Date().toISOString(),
    };
    save({ ...db, certRegistry: [...(db.certRegistry || []), record] });
    printTC(st, logo, db.settings, { tcNo: no, dt: new Date(tcDt).toLocaleDateString('en-IN'), admDt: tcAdmDt ? new Date(tcAdmDt).toLocaleDateString('en-IN') : st.ad ? new Date(st.ad).toLocaleDateString('en-IN') : '', ld: tcLd ? new Date(tcLd).toLocaleDateString('en-IN') : '', reason: tcRs, conduct: tcCo, feeStatus: tcFe, attP: p, attT: t });
    toast('TC Generated & Saved');
  };
  const doCC = () => {
    if (!ccStu) { toast('Select student', 'err'); return; }
    const st = db.students.find(x => x.id === ccStu); if (!st) return;
    const attP = ccAttP ? parseInt(ccAttP) : 0;
    const attT = ccAttT ? parseInt(ccAttT) : 0;
    const no = ccNo || genNextNo('cc');
    const record = {
      id: 'CC' + uid(), type: 'cc', no, dt: new Date(ccDt).toLocaleDateString('en-IN'),
      stuId: st.id, name: st.fn + (st.ln ? ' ' + st.ln : ''), cls: st.cls, roll: st.roll || '',
      admno: st.admno || '', conduct: ccCo, purpose: ccPu, generatedAt: new Date().toISOString(),
    };
    save({ ...db, certRegistry: [...(db.certRegistry || []), record] });
    printCC(st, logo, db.settings, { ccNo: no, dt: new Date(ccDt).toLocaleDateString('en-IN'), conduct: ccCo, purpose: ccPu, remarks: ccRm, attP, attT });
    toast('CC Generated & Saved');
  };
  const doBonafide = () => {
    if (!bcStu) { toast('Select student', 'err'); return; }
    const st = db.students.find(x => x.id === bcStu); if (!st) return;
    const no = bcNo || genNextNo('bc');
    const record = {
      id: 'BC' + uid(), type: 'bc', no, dt: new Date(bcDt).toLocaleDateString('en-IN'),
      stuId: st.id, name: st.fn + (st.ln ? ' ' + st.ln : ''), cls: st.cls, roll: st.roll || '',
      admno: st.admno || '', purpose: bcPurpose, generatedAt: new Date().toISOString(),
    };
    save({ ...db, certRegistry: [...(db.certRegistry || []), record] });
    printBonafide(st, logo, db.settings, { bcNo: no, dt: new Date(bcDt).toLocaleDateString('en-IN'), purpose: bcPurpose, cls: bcClsOverride || st.cls, dob: bcDobOverride || st.dob, admno: bcAdmnoOverride || st.admno });
    toast('Bonafide Certificate Generated & Saved');
  };

  return (
    <div className="documents-page p-6 space-y-8">
      <img src={LOGO_SRC} alt="" className="hidden" onError={() => setLogoErr(true)} onLoad={() => setLogoErr(false)} />

      {/* Page Header */}
      <div>
        <h2 className="text-5xl font-extrabold text-primary tracking-tight" style={{ fontFamily: 'Manrope,sans-serif' }}>Document Studio</h2>
        <p className="text-on-surface-variant mt-1 font-medium tracking-wide text-sm">Batch generate identification cards and certificates for the academic year.</p>
      </div>

      {/* Template Toggle */}
      <div className="documents-tabs flex items-center gap-8 border-b border-surface-variant/30 pb-0">
        {[['id', 'badge', 'ID Card'], ['tid', 'badge', 'Teacher ID Card'], ['tc', 'workspace_premium', 'Transfer Certificate'], ['cc', 'verified_user', 'Character Certificate'], ['bc', 'verified', 'Bonafide Certificate'], ['reg', 'history', 'Registry']].map(([id, icon, label]) => (
          <button key={id} onClick={() => setDocTab(id)}
            className={'flex items-center gap-2 pb-4 font-extrabold text-base transition-all border-0 bg-transparent cursor-pointer relative ' + (docTab === id ? 'text-primary' : 'text-on-surface-variant/50 hover:text-on-surface-variant')}
            style={{ fontFamily: 'Manrope,sans-serif' }}>
            <span className="material-symbols-outlined text-lg">{icon}</span>
            {label}
            {docTab === id && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-full" />}
          </button>
        ))}
      </div>

      {/* ID CARD TAB */}
      {docTab === 'id' && (
        <div className="documents-grid grid grid-cols-12 gap-8">
          <div className="documents-form-column col-span-5 flex flex-col gap-6">
            <div className="documents-form-card bg-surface-container-low rounded-2xl p-6">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant mb-5">Configuration</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-on-surface-variant mb-1.5">Academic Class</label>
                  <select value={selCls} onChange={e => { setSelCls(e.target.value); setSelStu(''); }}
                    className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                    <option value="">All Classes</option>
                    {classes.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="documents-two-grid grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-on-surface-variant mb-1.5">School Phone</label>
                    <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number"
                      className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
                  </div>
                  <div>
                    <label className="block text-sm text-on-surface-variant mb-1.5">Academic Year</label>
                    <input value={year} onChange={e => setYear(e.target.value)}
                      className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-on-surface-variant mb-1.5">Principal Name</label>
                  <input value={prin} onChange={e => setPrin(e.target.value)} placeholder="Principal"
                    className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
                </div>
                <div>
                  <label className="block text-sm text-on-surface-variant mb-1.5">Card Theme</label>
                  <select value={theme} onChange={e => setTheme(e.target.value)}
                    className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                    <option value="blue">Blue (Default)</option>
                    <option value="green">Green</option>
                    <option value="maroon">Maroon / Red</option>
                    <option value="navy">Navy</option>
                    <option value="purple">Purple</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="documents-student-list bg-surface-container-lowest rounded-2xl border border-surface-container-high overflow-hidden flex-1">
              <div className="flex justify-between items-center px-5 py-4 border-b border-surface-container-low">
                <span className="text-sm font-bold text-primary" style={{ fontFamily: 'Manrope,sans-serif' }}>Student List</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selAll} onChange={e => setSelAll(e.target.checked)} className="rounded border-outline text-primary focus:ring-primary" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Select All</span>
                </label>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-surface-container-low">
                {clsStudents.map((st) => (
                  <div key={st.id} onClick={() => setSelStu(st.id)}
                    className={'flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors group ' + (selStu === st.id ? 'bg-primary/5' : 'hover:bg-surface-container-low')}>
                    <input type="checkbox" checked={selAll || selStu === st.id} readOnly className="rounded border-outline text-primary focus:ring-primary" />
                    <div className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                      {st.fn?.[0]}{st.ln?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-on-surface truncate">{st.fn} {st.ln}</p>
                      <p className="text-[10px] text-on-surface-variant">Roll: {st.roll} · {st.cls}</p>
                    </div>
                    <span className="text-[10px] font-bold text-on-surface-variant/40 opacity-0 group-hover:opacity-100 uppercase">Select</span>
                  </div>
                ))}
                {!clsStudents.length && <div className="px-5 py-8 text-center text-sm text-on-surface-variant">No students</div>}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={async () => { if (!selStu) { toast('Select a student', 'err'); return; } printCard(s, photo, logo, phone, year, prin, theme); try { await markIdCardGenerated(activeSessionId, s.sid || s.id, { theme, year, prin, phone }); } catch (e) { } }}
                className="w-full py-4 bg-gradient-to-br from-primary to-primary-container text-white rounded-xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-0 cursor-pointer hover:opacity-90 transition-opacity"
                style={{ fontFamily: 'Manrope,sans-serif' }}>
                <span className="material-symbols-outlined text-lg">id_card</span>
                Generate ID Card
              </button>
              <button onClick={() => {
                if (!selCls) { toast('Select a class first', 'err'); return; }
                const sts = db.students.filter(x => x.cls === selCls);
                printClassCards(sts, db.photos, logo, phone, year, prin, theme);
                toast('Printing ' + sts.length + ' cards');
              }}
                className="w-full py-4 bg-surface-container-highest text-primary rounded-xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-0 cursor-pointer hover:bg-surface-variant transition-colors"
                style={{ fontFamily: 'Manrope,sans-serif' }}>
                <span className="material-symbols-outlined text-lg">print</span>
                Print Entire Class
              </button>
            </div>
          </div>
          <div className="documents-preview-column col-span-7 flex flex-col gap-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant">Live Preview</p>
            <div className="documents-preview-wrap flex gap-6 items-start flex-wrap">
              {[['Front Face', previewHTML], ['Back Face', previewBackHTML]].map(([label, html]) => {
                const scale = 0.46;
                const W = Math.round(CARD_DIMS.CW * scale);
                const H = Math.round(CARD_DIMS.CH * scale);
                const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:transparent;}</style></head><body>${html}</body></html>`;
                return (
                  <div key={label} className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">{label}</span>
                    <div className="bg-surface-container-low rounded-2xl" style={{ width: W + 16, height: H + 16, padding: 8, overflow: 'hidden', flexShrink: 0 }}>
                      <iframe
                        srcDoc={doc}
                        scrolling="no"
                        style={{ width: CARD_DIMS.CW, height: CARD_DIMS.CH, border: 'none', transform: `scale(${scale})`, transformOrigin: 'top left', display: 'block', flexShrink: 0 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TEACHER ID CARD TAB */}
      {docTab === 'tid' && (
        <div className="documents-grid grid grid-cols-12 gap-8">
          <div className="documents-form-column col-span-5 flex flex-col gap-6">
            <div className="documents-form-card bg-surface-container-low rounded-2xl p-6 space-y-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant mb-2">Teacher ID Card</p>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Select Teacher *</label>
                <select value={tidStu} onChange={e => setTidStu(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                  <option value="">— Select Teacher —</option>
                  {db.teachers.map(t => <option key={t.id} value={t.id}>{t.fn} {t.ln} {t.su ? '(' + t.su + ')' : ''}</option>)}
                </select>
              </div>
              {tidStu && (() => {
                const t = db.teachers.find(x => x.id === tidStu); return t ? (
                  <div className="bg-surface-container-lowest rounded-xl p-4 space-y-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-2">Auto-filled</p>
                    {[['Name', t.fn + ' ' + t.ln], ['Designation', t.desig || 'Teacher'], ['Subject', t.su || '—'], ['Qualification', t.qual || '—'], ['Emp. ID', t.empId || '—'], ['Phone', t.ph || '—']].map(([l, v]) => (
                      <div key={l} className="flex gap-2 text-xs"><span className="text-on-surface-variant w-28 flex-shrink-0">{l}</span><span className="font-semibold text-on-surface">{v}</span></div>
                    ))}
                  </div>
                ) : null;
              })()}
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Card Theme</label>
                <select value={tidTheme} onChange={e => setTidTheme(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                  <option value="blue">Blue (Default)</option>
                  <option value="green">Green</option>
                  <option value="maroon">Maroon / Red</option>
                  <option value="navy">Navy</option>
                  <option value="purple">Purple</option>
                </select>
              </div>
              <button onClick={() => {
                if (!tidStu) { toast('Select a teacher', 'err'); return; }
                const t = db.teachers.find(x => x.id === tidStu); if (!t) return;
                const tphoto = (db.tphotos || {})[t.id] || null;
                printTeacherCard(t, tphoto, logo, phone, year, prin, tidTheme);
              }} className="w-full py-4 bg-gradient-to-br from-primary to-primary-container text-white rounded-xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-0 cursor-pointer hover:opacity-90 transition-opacity"
                style={{ fontFamily: 'Manrope,sans-serif' }}>
                <span className="material-symbols-outlined text-lg">badge</span>
                Generate Teacher ID Card
              </button>
            </div>
          </div>
          <div className="documents-preview-column documents-preview-panel col-span-7 bg-surface-container-low rounded-2xl p-4 flex flex-col gap-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant px-1">Live Preview</p>
            {(() => {
              const t = tidStu ? db.teachers.find(x => x.id === tidStu) : null;
              const tphoto = t ? (db.tphotos || {})[t.id] || null : null;
              const demoT = { fn: 'Teacher', ln: 'Name', su: 'Subject', cls: '—', qual: 'B.Ed', empId: '—', desig: 'Teacher', ph: '—', blood: '' };
              const previewT = t || demoT;
              const frontHTML = buildTeacherCard(previewT, tphoto, logo, phone, year, prin, tidTheme);
              const backHTML = buildTeacherCardBack(previewT, logo, phone);
              const scale = 0.46;
              const W = Math.round(CARD_DIMS.CW * scale);
              const H = Math.round(CARD_DIMS.CH * scale);
              return (
                <div className="documents-preview-wrap flex gap-6 items-start flex-wrap">
                  {[['Front Face', frontHTML], ['Back Face', backHTML]].map(([label, html]) => {
                    const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:transparent;}</style></head><body>${html}</body></html>`;
                    return (
                      <div key={label} className="flex flex-col gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">{label}</span>
                        <div className="bg-surface-container-low rounded-2xl" style={{ width: W + 16, height: H + 16, padding: 8, overflow: 'hidden', flexShrink: 0 }}>
                          <iframe srcDoc={doc} scrolling="no"
                            style={{ width: CARD_DIMS.CW, height: CARD_DIMS.CH, border: 'none', transform: `scale(${scale})`, transformOrigin: 'top left', display: 'block', flexShrink: 0 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* TRANSFER CERTIFICATE TAB */}
      {docTab === 'tc' && (
        <div className="documents-grid grid grid-cols-12 gap-8">
          <div className="documents-form-column documents-form-card col-span-5 bg-surface-container-low rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant">Transfer Certificate</p>

            {/* Student selector */}
            <div>
              <label className="block text-sm text-on-surface-variant mb-1.5">Filter by Class</label>
              <select value={tcCls || ''} onChange={e => { setTcCls(e.target.value); setTcStu(''); }}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm mb-3">
                <option value="">— All Classes —</option>
                {db.classes.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <label className="block text-sm text-on-surface-variant mb-1.5">Student *</label>
              <select value={tcStu} onChange={e => {
                const id = e.target.value;
                setTcStu(id);
                const st = db.students.find(x => x.id === id);
                if (st?.ad) setTcAdmDt(st.ad);
              }}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                <option value="">— Select Student —</option>
                {db.students.filter(st => !tcCls || st.cls === tcCls).map(st => <option key={st.id} value={st.id}>{st.fn} {st.ln} ({st.cls})</option>)}
              </select>
            </div>

            {/* Auto-filled student data preview */}
            {tcStu && (() => {
              const st = db.students.find(x => x.id === tcStu); return st ? (
                <div className="bg-surface-container-lowest rounded-xl p-4 space-y-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-2">Auto-filled from student record</p>
                  {[
                    ['Name', st.fn + ' ' + st.ln],
                    ['Father', st.father || '—'],
                    ['Mother', st.mother || '—'],
                    ['Class / Roll', (st.cls || '—') + ' / ' + (st.roll || '—')],
                    ['Adm. No.', st.admno || '—'],
                    ['PEN No.', st.pen || '—'],
                    ['APAAR ID', st.apaar || '—'],
                    ['Previous School', st.ps || '—'],
                    ['DOB', fmtDob(st.dob)],
                    ['Address', (st.addr && st.addr !== 'admin') ? (st.addr + (st.city ? ', ' + st.city : '')) : (st.city || '—')],
                    ['Blood Group', st.blood || '—'],
                    ['Aadhaar', st.aadhar || '—'],
                  ].map(([l, v]) => (
                    <div key={l} className="flex gap-2 text-xs">
                      <span className="text-on-surface-variant w-24 flex-shrink-0">{l}</span>
                      <span className="font-semibold text-on-surface">{v}</span>
                    </div>
                  ))}
                </div>
              ) : null;
            })()}

            {/* Fields NOT in student directory — must ask */}
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant pt-1">Additional details required</p>
            <div className="documents-two-grid grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Academic Session</label>
                <input value={db.settings.year || ''} readOnly
                  className="w-full p-3 bg-surface-container-low rounded-xl border-none text-sm text-primary font-semibold cursor-default" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">TC Number <span style={{ fontSize: 10, color: '#94a3b8' }}>(auto-generated if blank)</span></label>
                <input value={tcNo} onChange={e => setTcNo(e.target.value)} placeholder={genNextNo('tc')}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Issue Date</label>
                <input type="date" value={tcDt} onChange={e => setTcDt(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Date of Admission</label>
                <input type="date" value={tcAdmDt} onChange={e => setTcAdmDt(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Conduct</label>
                <select value={tcCo} onChange={e => setTcCo(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                  {['Good', 'Very Good', 'Excellent', 'Satisfactory'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Fee Status</label>
                <select value={tcFe} onChange={e => setTcFe(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                  {['All Dues Cleared', 'Dues Pending'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-on-surface-variant mb-1.5">Reason for Leaving</label>
              <select value={tcRs} onChange={e => setTcRs(e.target.value)}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                {["Parents' Transfer", "Change of Residence", "Admission to Other School", "Completion of Studies", "Family Reasons"].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>

            <button onClick={doTC}
              className="w-full py-4 bg-gradient-to-br from-primary to-primary-container text-white rounded-xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-0 cursor-pointer hover:opacity-90 transition-opacity mt-2"
              style={{ fontFamily: 'Manrope,sans-serif' }}>
              <span className="material-symbols-outlined text-lg">workspace_premium</span>
              Generate Transfer Certificate
            </button>
          </div>
          <div className="documents-preview-column documents-preview-panel col-span-7 bg-surface-container-low rounded-2xl p-4 flex flex-col gap-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant px-1">Live Preview</p>
            {(() => {
              const st = tcStu ? db.students.find(x => x.id === tcStu) : null;
              const previewSt = st || { fn: 'Student', ln: 'Name', father: 'Father Name', mother: 'Mother Name', cls: '—', roll: '—', admno: '—', dob: '', addr: '', city: '', blood: '', aadhar: '', caste: '', religion: '' };
              const { p: attPv, t: attTv } = tcStu ? getAtt(tcStu) : { p: 0, t: 0 };
              const html = buildTC(previewSt, logo, db.settings, {
                tcNo: tcNo || 'TC-2025-001',
                dt: new Date(tcDt).toLocaleDateString('en-IN'),
                admDt: tcAdmDt ? new Date(tcAdmDt).toLocaleDateString('en-IN') : '—',
                ld: tcLd ? new Date(tcLd).toLocaleDateString('en-IN') : '—',
                reason: tcRs, conduct: tcCo, feeStatus: tcFe,
                attP: attPv, attT: attTv,
              });
              const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;background:#fff;}</style></head>${html}</html>`;
              return <DocPreview srcDoc={srcDoc} docW={820} docH={1100} />;
            })()}
          </div>
        </div>
      )}

      {/* CHARACTER CERTIFICATE TAB */}
      {docTab === 'cc' && (
        <div className="documents-grid grid grid-cols-12 gap-8">
          <div className="documents-form-column documents-form-card col-span-5 bg-surface-container-low rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant">Character Certificate</p>
            <div>
              <label className="block text-sm text-on-surface-variant mb-1.5">Filter by Class</label>
              <select value={ccCls || ''} onChange={e => { setCcCls(e.target.value); setCcStu(''); }}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm mb-3">
                <option value="">— All Classes —</option>
                {db.classes.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <label className="block text-sm text-on-surface-variant mb-1.5">Student *</label>
              <select value={ccStu} onChange={e => setCcStu(e.target.value)}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                <option value="">— Select Student —</option>
                {db.students.filter(st => !ccCls || st.cls === ccCls).map(st => <option key={st.id} value={st.id}>{st.fn} {st.ln} ({st.cls})</option>)}
              </select>
            </div>
            {ccStu && (() => {
              const st = db.students.find(x => x.id === ccStu); return st ? (
                <div className="bg-surface-container-lowest rounded-xl p-4">
                  <div className="text-sm font-bold text-on-surface">{st.fn} {st.ln}</div>
                  <div className="text-xs text-on-surface-variant mt-0.5">Class: {st.cls} · Roll: {st.roll}</div>
                </div>
              ) : null;
            })()}
            <div className="documents-two-grid grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">CC Number <span style={{ fontSize: 10, color: '#94a3b8' }}>(auto-generated if blank)</span></label>
                <input value={ccNo} onChange={e => setCcNo(e.target.value)} placeholder={genNextNo('cc')}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Issue Date</label>
                <input type="date" value={ccDt} onChange={e => setCcDt(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Conduct</label>
                <select value={ccCo} onChange={e => setCcCo(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                  {['Good', 'Very Good', 'Excellent', 'Satisfactory'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Purpose</label>
                <select value={ccPu} onChange={e => setCcPu(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                  {['General Purpose', 'Admission', 'College Admission', 'Job Application', 'Scholarship'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            {/* Attendance - manual entry */}
            <div>
              <label className="block text-sm text-on-surface-variant mb-1.5">Attendance</label>
              <div className="flex gap-3 items-center">
                <div className="flex-1">
                  <input type="number" value={ccAttP} onChange={e => setCcAttP(e.target.value)} placeholder="Days attended"
                    className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
                </div>
                <span className="text-on-surface-variant font-medium">out of</span>
                <div className="flex-1">
                  <input type="number" value={ccAttT} onChange={e => setCcAttT(e.target.value)} placeholder="Total working days"
                    className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
                </div>
                {ccAttT > 0 && (
                  <span className="text-primary font-bold text-sm whitespace-nowrap">{ccAttPct}%</span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm text-on-surface-variant mb-1.5">Remarks</label>
              <input value={ccRm} onChange={e => setCcRm(e.target.value)} placeholder="e.g. Active in sports and cultural activities"
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
            </div>
            <button onClick={doCC}
              className="w-full py-4 bg-gradient-to-br from-primary to-primary-container text-white rounded-xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-0 cursor-pointer hover:opacity-90 transition-opacity mt-2"
              style={{ fontFamily: 'Manrope,sans-serif' }}>
              <span className="material-symbols-outlined text-lg">verified_user</span>
              Generate Character Certificate
            </button>
          </div>
          <div className="documents-preview-column documents-preview-panel col-span-7 bg-surface-container-low rounded-2xl p-4 flex flex-col gap-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant px-1">Live Preview</p>
            {(() => {
              const st = ccStu ? db.students.find(x => x.id === ccStu) : null;
              const previewSt = st || { fn: 'Student', ln: 'Name', father: 'Father Name', cls: '—', roll: '—' };
              const { p: attPv, t: attTv } = ccStu ? getAtt(ccStu) : { p: 0, t: 0 };
              const html = buildCC(previewSt, logo, db.settings, {
                ccNo: ccNo || 'CC-2025-001',
                dt: new Date(ccDt).toLocaleDateString('en-IN'),
                conduct: ccCo, purpose: ccPu, remarks: ccRm,
                attP: ccAttP ? parseInt(ccAttP) : 0,
                attT: ccAttT ? parseInt(ccAttT) : 0,
              });
              const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;background:#fff;}</style></head>${html}</html>`;
              return <DocPreview srcDoc={srcDoc} docW={820} docH={960} />;
            })()}
          </div>
        </div>
      )}

      {/* BONAFIDE CERTIFICATE TAB */}
      {docTab === 'bc' && (
        <div className="documents-grid grid grid-cols-12 gap-8">
          <div className="documents-form-column documents-form-card col-span-5 bg-surface-container-low rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant">Bonafide Certificate</p>
            <div>
              <label className="block text-sm text-on-surface-variant mb-1.5">Filter by Class</label>
              <select value={bcCls || ''} onChange={e => { setBcCls(e.target.value); setBcStu(''); }}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm mb-3">
                <option value="">— All Classes —</option>
                {db.classes.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <label className="block text-sm text-on-surface-variant mb-1.5">Student *</label>
              <select value={bcStu} onChange={e => setBcStu(e.target.value)}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                <option value="">— Select Student —</option>
                {db.students.filter(st => !bcCls || st.cls === bcCls).map(st => <option key={st.id} value={st.id}>{st.fn} {st.ln} ({st.cls})</option>)}
              </select>
            </div>
            {bcStu && (() => {
              const st = db.students.find(x => x.id === bcStu); return st ? (
                <div className="bg-surface-container-lowest rounded-xl p-4 space-y-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-2">Auto-filled from student record</p>
                  {[['Name', st.fn + ' ' + st.ln], ['Father', st.father || '—'], ['Class', st.cls || '—'], ['Adm. No.', st.admno || '—'], ['DOB', fmtDob(st.dob)], ['Address', (st.addr && st.addr !== 'admin') ? (st.addr + (st.city ? ', ' + st.city : '')) : (st.city || '—')], ['Aadhaar', st.aadhar || '—']].map(([l, v]) => (
                    <div key={l} className="flex gap-2 text-xs"><span className="text-on-surface-variant w-24 flex-shrink-0">{l}</span><span className="font-semibold text-on-surface">{v}</span></div>
                  ))}
                </div>
              ) : null;
            })()}
            <div className="documents-two-grid grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Certificate No. <span style={{ fontSize: 10, color: '#94a3b8' }}>(auto if blank)</span></label>
                <input value={bcNo} onChange={e => setBcNo(e.target.value)} placeholder={genNextNo('bc')}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Issue Date</label>
                <input type="date" value={bcDt} onChange={e => setBcDt(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-on-surface-variant mb-1.5">Class <span style={{ fontSize: 10, color: '#94a3b8' }}>(override if needed)</span></label>
                <input value={bcClsOverride} onChange={e => setBcClsOverride(e.target.value)}
                  placeholder={bcStu ? (db.students.find(x => x.id === bcStu)?.cls || 'e.g. 8th') : 'e.g. 8th'}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Date of Birth <span style={{ fontSize: 10, color: '#94a3b8' }}>(override)</span></label>
                <input type="date" value={bcDobOverride} onChange={e => setBcDobOverride(e.target.value)}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
              <div>
                <label className="block text-sm text-on-surface-variant mb-1.5">Admission No. <span style={{ fontSize: 10, color: '#94a3b8' }}>(override)</span></label>
                <input value={bcAdmnoOverride} onChange={e => setBcAdmnoOverride(e.target.value)}
                  placeholder={bcStu ? (db.students.find(x => x.id === bcStu)?.admno || '') : ''}
                  className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-sm text-on-surface" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-on-surface-variant mb-1.5">Purpose of Certificate</label>
              <select value={bcPurpose} onChange={e => setBcPurpose(e.target.value)}
                className="w-full p-3 bg-surface-container-lowest rounded-xl border-none focus:ring-2 focus:ring-primary/20 text-on-surface text-sm">
                {['Aadhaar Card Enrollment', 'Bank Account Opening', 'Passport Application', 'Scholarship Application', 'Other Official Uses'].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <button onClick={doBonafide}
              className="w-full py-4 bg-gradient-to-br from-primary to-primary-container text-white rounded-xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-0 cursor-pointer hover:opacity-90 transition-opacity mt-2"
              style={{ fontFamily: 'Manrope,sans-serif' }}>
              <span className="material-symbols-outlined text-lg">verified</span>
              Generate Bonafide Certificate
            </button>
          </div>
          <div className="documents-preview-column documents-preview-panel col-span-7 bg-surface-container-low rounded-2xl p-4 flex flex-col gap-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant px-1">Live Preview</p>
            {(() => {
              const st = bcStu ? db.students.find(x => x.id === bcStu) : null;
              const previewSt = st || { fn: 'Student', ln: 'Name', father: 'Father Name', cls: '—', admno: '—', dob: '', addr: '', city: '', aadhar: '' };
              const html = buildBonafide(previewSt, logo, db.settings, { bcNo: bcNo || 'BC-2026-001', dt: new Date(bcDt).toLocaleDateString('en-IN'), purpose: bcPurpose, cls: bcClsOverride || previewSt.cls, dob: bcDobOverride || previewSt.dob, admno: bcAdmnoOverride || previewSt.admno });
              const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;background:#fff;}</style></head>${html}</html>`;
              return <DocPreview srcDoc={srcDoc} docW={820} docH={1100} />;
            })()}
          </div>
        </div>
      )}

      {/* REGISTRY TAB */}
      {docTab === 'reg' && <CertRegistry db={db} save={save} />}
    </div>
  );
}


export default Documents;
